import { useMemo } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Svg, TST_COLUMN_SIZE } from "@impero-com/ui";
import RemoveIcon from "@impero-com/ui/img/remove.svg";
import {
    createColumnHelper,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { EntityListLinkedResources } from "api/generated/model";

import * as theme from "./ControlEntitiesTables.css";
import { buildSharedColumns, EntityTableRow } from "./utils";

export function useLinkedEntitiesTable(
    controlEntities: EntityTableRow[],
    linkedResources: EntityListLinkedResources,
    setAddEntitiesOpen: (open: boolean) => void,
    handleRemoveLinkedEntity: (entityId: number) => void,
    readonly = false
) {
    const { t } = useTranslation();
    const sharedColumns = useMemo(
        () => buildSharedColumns(t, linkedResources, setAddEntitiesOpen),
        [t, linkedResources, setAddEntitiesOpen]
    );
    const deleteColumn = useMemo(
        () => buildDeleteColumn(handleRemoveLinkedEntity),
        [handleRemoveLinkedEntity]
    );
    const columns = useMemo(
        () => (readonly ? sharedColumns : [...sharedColumns, ...deleteColumn]),
        [sharedColumns, deleteColumn, readonly]
    );
    const tableData: EntityTableRow[] = useMemo(() => {
        return controlEntities.map((entity) => ({
            entityId: entity.entityId,
            name: entity.name,
            tagIds: entity.tagIds,
            controlAccessRights: entity.controlAccessRights,
        }));
    }, [controlEntities]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel<EntityTableRow>(),
        getSortedRowModel: getSortedRowModel<EntityTableRow>(),
    });
    return table;
}
const COLUMN_ID_ACTIONS = "actions";

function buildDeleteColumn(
    handleRemoveLinkedEntity: (entityId: number) => void
) {
    const columnHelper = createColumnHelper<EntityTableRow>();

    return [
        columnHelper.display({
            id: COLUMN_ID_ACTIONS,
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },

            cell: (info) => {
                return (
                    <Button
                        variant="text"
                        size={"icon"}
                        icon={
                            <Svg
                                src={RemoveIcon}
                                className={theme.removeIcon}
                            />
                        }
                        onClick={() =>
                            handleRemoveLinkedEntity(info.row.original.entityId)
                        }
                        className={theme.removeButton}
                    />
                );
            },
        }),
    ];
}
